import React, { lazy, Suspense } from 'react';
import ErrorBoundary from '../../_modules/error/error_boundary';
import { handleView } from '../handle_view';
import ActiveProductBatchStocksStore from '../../_flux/stores/product_batch_stocks/active_product_batch_stocks_store';
import ActiveProductStore from '../../_flux/stores/active_product_store';
import ProductMixedStocksLightboxStore from '../../_flux/stores/product_mixed_stocks_lightbox_store';
import PostcodeStore from '../../_flux/stores/postcode_store';
import BillingAddressStore from '../../_flux/stores/billing_address_store';
import ShippingAddressStore from '../../_flux/stores/shipping_address_store';
import FittingOption from '../../_flux/stores/models/fitting_option';
import onCartMixedStocksComputeRequest from './on_cart_mixed_stocks_compute_request';
import { onChangePostcodeAction, postcodeValidAction, setPostcodeSuggestion } from '../../_flux/actions/postcode_actions';
import {
    setStreetAction, setStateAction, setPostcodeAction, setCountryAction, setInvalid, unsetInvalid,
    setAutoSuggestResult, clearAutoSuggestResult, setCityAction, setStreetNumberAction, setSuburbAction
} from '../../_flux/actions/shipping_address_actions';
import { PostcodeValidator } from '../../validator/postcode_validator';
import onPostcodeSubmit from './on_postcode_submit';
import onCallForAlternativeStockClick from './on_call_for_alternative_stock_click';
import onPayNowClick from './on_pay_now_click';
import onGoToPaymentClick from './on_go_to_payment_click';
import onShippingAddressClick from './on_save_shipping_address_click';
import onNoFittingOptionClick from './on_no_fitting_option_click';
import onCartShippingOptionSelectComputeRequest from './on_cart_shipping_option_select_compute_request';
import { show as showMatesratesLightbox } from '../../_flux/actions/friendbuy_light_box_actions';
import {
    closeAction as closeLightbox,
    setQuantityAction, showItemPriceDetails, showShippingPriceDetails,
    unsetErrorMessageAction, setFittingPriceInDetailModeAction, unsetFittingPriceInDetailModeAction,
    openProductLayerAction, setSelectedFittingOption,
    closeOrderFormLayerAction, closeOrderStatusLayerAction, closePostcodeLayerAction,
    closeShippingAddressFormLayerAction, withFittingOptionAction,
    showNoFittingShippingAddressSummary, hideNoFittingShippingAddressSummary,
    showNoFittingShippingAddressInput, openOrderFormLayerAction, unsetPaymentStatus, unsetTimerIntervalId,
    addFittingService, removeFittingService, clearFittingService,
} from '../../_flux/actions/product_mixed_stocks_lightbox_actions';
import GoogleDetails from '../../addressify/google/google_details';
import GoogleDetailsParser from '../../addressify/google/google_details_parser';
import CheckoutAddressify from '../../checkout/checkout_addressify';
import { validatePostcode } from '../../validator/PostcodeAutocompleteValidator';

const ProductMixedStocksComponent = React.lazy(() => import('../../_flux/components/product_mixed_stocks/product_mixed_stocks'));
const renderProductMixedStocks = (states, options) => (
    <ErrorBoundary>
        <Suspense fallback={<div>Loading...</div>}>
            <ProductMixedStocksComponent
                staticUrl={TYROOLA.globals.staticImagesUrl}
                currencyIsoCode={TYROOLA.globals.currencyIsoCode}
                store={TYROOLA.globals.store}
                state={states.state}
                postcodeState={states.postcodeState}
                activeProductState={states.activeProductState}
                activeProductBatchStocksState={states.activeProductBatchStocksState}
                billingAddressState={states.billingAddressState}
                shippingAddressState={states.shippingAddressState}
                onLightboxClose={onLightboxClose}
                onPostcodeChange={onPostcodeChange}
                onPostcodeSubmit={options.onPostcodeSubmit}
                onPostcodeQuantityChange={onPostcodeQuantityChange}
                onProductQuantityChange={onProductQuantityChange}
                onMatesRatesLearnMoreClick={showMatesratesLightbox}
                onShowItemPriceDetailsClick={showItemPriceDetails}
                onShowShippingPriceDetailsClick={showShippingPriceDetails}
                onCallForAlternativeStockClick={options.onCallForAlternativeStockClick}
                onGoToPaymentClick={options.onGoToPaymentClick}
                onPayNowClick={options.onPayNowClick}
                onLightboxBack={onLightboxBack}
                onFittingPriceDetailModeClick={onFittingPriceDetailModeClick}
                onFittingOptionSelect={onFittingOptionSelect}
                onNoFittingClick={options.onNoFittingClick}
                onShippingAddressClick={options.onShippingAddressClick}
                onAddressChange={onAddressChange}
                onAutoSuggestAddressSelect={onAutoSuggestAddressSelect}
                onFittingServiceChange={onFittingServiceChange}
                onOrderOverviewShowDetailClick={onOrderOverviewShowDetailClick}
                onTrackOrderStatusClick={onTrackOrderStatusClick}
                selectPostcodeSuggestion={setPostcodeSuggestion}
            />
        </Suspense>
    </ErrorBoundary>
);

const stores = {
    state: ProductMixedStocksLightboxStore,
    postcodeState: PostcodeStore,
    activeProductBatchStocksState: ActiveProductBatchStocksStore,
    activeProductState: ActiveProductStore,
    billingAddressState: BillingAddressStore,
    shippingAddressState: ShippingAddressStore,
};

const getOptions = options => ({
    ...options,
    onPostcodeSubmit: onPostcodeSubmit(),
    onCallForAlternativeStockClick: onCallForAlternativeStockClick(),
    onPayNowClick: onPayNowClick(),
    onNoFittingClick: onNoFittingOptionClick(),
    onShippingAddressClick: onShippingAddressClick(),
    onGoToPaymentClick: onGoToPaymentClick(),
});

const onLightboxClose = () => {
    clearAutoSuggestResult();
    closeLightbox();
};

const onLightboxBack = () => {
    const { orderFormLayerVisible, shippingAddressFormLayerVisible, orderStatusLayerVisible, timerIntervalId }
        = ProductMixedStocksLightboxStore.getState();
    closePostcodeLayerAction();
    closeOrderStatusLayerAction();
    if (true === shippingAddressFormLayerVisible) {
        clearAutoSuggestResult();
        closeShippingAddressFormLayerAction();
        openProductLayerAction();
    } else if (true === orderFormLayerVisible) {
        unsetErrorMessageAction();
        closeOrderFormLayerAction();
        openProductLayerAction();
    } else if (true === orderStatusLayerVisible) {
        if (null !== timerIntervalId) {
            clearInterval(timerIntervalId);
            unsetTimerIntervalId();
            unsetPaymentStatus();
        }
        unsetErrorMessageAction();
        closeOrderStatusLayerAction();
        openOrderFormLayerAction();
    }
};

const onPostcodeChange = value => {
    const { invalid, postcodeSuggestion, touched } = PostcodeStore.getState();
    onChangePostcodeAction(value);
    if (invalid && validatePostcode(TYROOLA.globals.store, value, postcodeSuggestion, touched)) {
        postcodeValidAction();
    }
};

const onPostcodeQuantityChange = quantity => {
    setQuantityAction(quantity);
};

const onProductQuantityChange = quantity => {
    const { appliedPostcode } = stores.postcodeState.getState();
    const { activeProductBatchStocks } = stores.activeProductBatchStocksState.getState();
    unsetErrorMessageAction();
    unsetFittingPriceInDetailModeAction();
    setQuantityAction(quantity);
    onCartMixedStocksComputeRequest(appliedPostcode, activeProductBatchStocks.id.value, quantity);
};

const onFittingPriceDetailModeClick = fsId => {
    setFittingPriceInDetailModeAction(fsId);
};

const onFittingOptionSelect = fsId => {
    withFittingOptionAction();
    setSelectedFittingOption(fsId);
    const { cart, fittingOptions } = ProductMixedStocksLightboxStore.getState();
    clearFittingService();
    fittingOptions.map((fittingOption) => {
        if (fittingOption instanceof FittingOption && fittingOption.fs.fsId === fsId) {
            fittingOption.services.map((service) => {
                addFittingService(service.type);
            });
        }
    });

    onCartShippingOptionSelectComputeRequest(cart);
};

const onAddressChange = address => {
    if (typeof window.googlePlace !== 'undefined') {
        const service = window.googlePlace;
        hideNoFittingShippingAddressSummary();
        if (address.length > 4) {
            service.predict(address, function callback(predictions, status) {
                if ('OK' === status.toUpperCase() && predictions) {
                    setAutoSuggestResult(predictions);
                } else {
                    clearAutoSuggestResult();
                }
            });
        }
        setStreetAction(address);
    } else {
        console.error('Can not initiate google place');
    }
};


const onAutoSuggestAddressSelect = placeId => {
    clearAutoSuggestResult();
    const service = new GoogleDetails();
    const placeDetailParser = new GoogleDetailsParser();
    service.get(placeId, function callback(result, status) {
        const address = placeDetailParser.parse(result);
        if (typeof address['route'] === 'undefined' ||
            typeof address['country'] === 'undefined' ||
            typeof address['postal_code'] === 'undefined' ||
            typeof address['locality'] === 'undefined' ||
            typeof address['administrative_area_level_1'] === 'undefined' ||
            (CheckoutAddressify.showSuburb() && typeof address['sublocality_level_1'] === 'undefined')) {
            setInvalid('street');
        } else {
            unsetInvalid('street');
            showNoFittingShippingAddressInput();
            showNoFittingShippingAddressSummary();
            setStreetAction(address.route);
            setStateAction(address.administrative_area_level_1);
            setCountryAction(address.country);
            setPostcodeAction(address.postal_code);
            setCityAction(address.locality);
            setStreetNumberAction('');
            if (typeof address['street_number'] !== 'undefined') {
                setStreetNumberAction(address.street_number);
            }

            setSuburbAction('');
            if (CheckoutAddressify.showSuburb()
                && typeof address['sublocality_level_1'] !== 'undefined'
                && address['sublocality_level_1'].length > 0) {
                setSuburbAction(address.sublocality_level_1);
            }
        }
    });
};

const onFittingServiceChange = (action, service) => {
    if (action === 'remove') removeFittingService(service);
    else if (action === 'add') addFittingService(service);
};

const onOrderOverviewShowDetailClick = () => {
    closeOrderFormLayerAction();
    openProductLayerAction();
};

const onTrackOrderStatusClick = (orderNumber) => {
    window.open(`/order/${orderNumber.toLowerCase()}/`, '_blank');
};

export const init = (container, options) =>
    handleView(stores, renderProductMixedStocks, getOptions(options), container);